import React, { useEffect, useState, useCallback } from "react";
import { MenuItem, Select } from "@mui/material";
import { MyCalendarIcon, MyCreditCardIcon } from "../../Assets/svg/MyIcons";
import { listTripolaPlansForDropdownAction } from "../../Actions/TripolaPlanActions";
import { CustomSwitch } from "../../Assets/customTheme/customswitch";
import { stepperTextFieldSx } from "../Tenant/stepperFormStyles";
import "./TripolaStep.css";

const formatMoney = (amount, symbol = "$") => {
	if (amount === null || amount === undefined || amount === "" || Number.isNaN(Number(amount))) {
		return "-";
	}
	return `${symbol} ${Number(amount).toFixed(2)}`;
};

const displayText = (value, fallback = "-") => {
	if (value === null || value === undefined) return fallback;
	const text = String(value).trim();
	if (!text || text.toLowerCase() === "null" || text.toLowerCase() === "undefined") {
		return fallback;
	}
	return text;
};

const getFeatureLabels = (features) => {
	if (!features) return [];
	let parsed = features;
	if (typeof features === "string") {
		try {
			parsed = JSON.parse(features);
		} catch {
			return features.trim() ? [features] : [];
		}
	}

	// New nested object shape from tripola_plan.features
	if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
		const labels = [];
		Object.entries(parsed).forEach(([key, value]) => {
			if (value && typeof value === "object" && value.enabled) {
				labels.push(
					key
						.split("_")
						.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
						.join(" ")
				);
			}
		});
		return labels;
	}

	if (!Array.isArray(parsed)) return [];
	return parsed
		.map((item) => {
			if (typeof item === "string") return item;
			if (item && typeof item === "object") {
				return (
					item.moduleName ||
					item.name ||
					item.feature ||
					item.label ||
					item.title ||
					""
				);
			}
			return "";
		})
		.filter(Boolean);
};

const TripolaPlanStructureStep = ({
	formik,
	selectedCurrency
}) => {
	const [tripolaPlanList, setTripolaPlanList] = useState([]);

	const selectedTripolaPlan = tripolaPlanList.find(
		(plan) => String(plan.id) === String(formik.values.tripolaPlanId)
	);

	const currencySymbol =
		selectedCurrency?.symbol ||
		selectedCurrency?.currencySymbol ||
		"$";

	const billingCycle = String(formik.values.tripolaBillingCycle || "").toLowerCase();
	const activePrice =
		billingCycle === "yearly"
			? selectedTripolaPlan?.yearly_price
			: selectedTripolaPlan?.monthly_price;
	const featureLabels = getFeatureLabels(selectedTripolaPlan?.features);

	const fetchTripolaPlansDropDownList = useCallback(async () => {
		try {
			const response = await listTripolaPlansForDropdownAction();
			setTripolaPlanList(response?.data?.data || []);
		} catch (error) {
			console.error("error fetching tripola plans ->", error);
		}
	}, []);

	useEffect(() => {
		fetchTripolaPlansDropDownList();
	}, [fetchTripolaPlansDropDownList]);

	return (
		<div className="tr-plan-layout">
			<div className="tr-plan-form">
				<section className="tr-step-section">
					<div className="tr-step-section-head">
						<h3>Plan selection</h3>
						<p>Choose a Tripola plan and billing cycle.</p>
					</div>
					<div className="tr-step-grid">
						<div className="tr-step-field is-full">
							<label>
								Select Plan<span className="tr-step-req">*</span>
							</label>
							<Select
								fullWidth
								name="tripolaPlanId"
								value={formik.values.tripolaPlanId || ""}
								onChange={(e) => {
									const selectedPlanId = e.target.value;

									formik.setFieldValue("tripolaPlanId", selectedPlanId);

									const selectedPlan = tripolaPlanList.find(
										(plan) => String(plan.id) === String(selectedPlanId)
									);

									if (selectedPlan?.selectedBillingCycle) {
										formik.setFieldValue(
											"tripolaBillingCycle",
											selectedPlan.selectedBillingCycle
										);
									} else {
										formik.setFieldValue("tripolaBillingCycle", "");
									}
								}}
								displayEmpty
								renderValue={(selected) => {
									if (!selected)
										return <span className="tr-step-placeholder">Select a plan</span>;
									const plan = tripolaPlanList.find(
										(p) => String(p.id) === String(selected)
									);
									return plan ? plan.plan_name : "";
								}}
								disabled={Boolean(tripolaPlanList.length === 0)}
								sx={stepperTextFieldSx["& .MuiOutlinedInput-root"]}
							>
								<MenuItem value="" disabled>
									<em>Select a plan</em>
								</MenuItem>
								{tripolaPlanList.map((plan) => (
									<MenuItem
										key={`plan-${plan.id}`}
										value={plan.id}
									>
										{plan.plan_name}
									</MenuItem>
								))}
							</Select>
							{formik.touched.tripolaPlanId && formik.errors.tripolaPlanId && (
								<span className="tr-step-msg is-error">
									{formik.errors.tripolaPlanId}
								</span>
							)}
						</div>

						<div className="tr-step-field is-full">
							<label>
								Billing Cycle<span className="tr-step-req">*</span>
							</label>
							<Select
								fullWidth
								name="tripolaBillingCycle"
								value={formik.values.tripolaBillingCycle || ""}
								onChange={formik.handleChange}
								displayEmpty
								disabled={Boolean(!formik.values.tripolaPlanId)}
								renderValue={(selected) => {
									if (!selected) {
										return (
											<span className="tr-step-placeholder">
												Select billing cycle
											</span>
										);
									}
									return selected;
								}}
								sx={stepperTextFieldSx["& .MuiOutlinedInput-root"]}
							>
								<MenuItem value="" disabled>
									<em>Select billing cycle</em>
								</MenuItem>
								<MenuItem value="monthly">
									Monthly
								</MenuItem>
								<MenuItem value="yearly">
									Yearly
								</MenuItem>
							</Select>
							{formik.touched.tripolaBillingCycle &&
								formik.errors.tripolaBillingCycle && (
									<span className="tr-step-msg is-error">
										{formik.errors.tripolaBillingCycle}
									</span>
								)}
						</div>

						<div className="tr-step-field is-full">
							<label>Enable Auto Pay</label>
							<div
								className={`tr-step-toggle${
									formik.values.tripolaIsAutoPay ? " is-on" : ""
								}`}
							>
								<div className="tr-step-toggle-copy">
									<strong>Auto Pay</strong>
									<span>
										{formik.values.tripolaIsAutoPay
											? "Payments renew automatically"
											: "Manual payment collection"}
									</span>
								</div>
								<CustomSwitch
									name="tripolaIsAutoPay"
									checked={formik.values.tripolaIsAutoPay}
									onChange={(event) =>
										formik.setFieldValue("tripolaIsAutoPay", event.target.checked)
									}
								/>
							</div>
						</div>
					</div>
				</section>
			</div>

			<aside className="tr-plan-preview">
				<div className="tr-plan-preview-head">
					<span className="tr-plan-preview-icon" aria-hidden>
						<MyCreditCardIcon />
					</span>
					<h3>{displayText(selectedTripolaPlan?.plan_name, "No Plan Selected")}</h3>
					{selectedTripolaPlan?.plan_description ? (
						<p>{displayText(selectedTripolaPlan.plan_description)}</p>
					) : (
						<p>Select a plan to preview pricing and features.</p>
					)}
					{selectedTripolaPlan && (
						<div className="tr-plan-price">
							{formatMoney(activePrice, currencySymbol)}
							{billingCycle ? (
								<small>/ {billingCycle}</small>
							) : null}
						</div>
					)}
				</div>

				<div className="tr-plan-preview-body">
					{!selectedTripolaPlan ? (
						<div className="tr-plan-empty">
							Choose a plan on the left to see details here.
						</div>
					) : (
						<>
							<div className="tr-plan-block">
								<h4>Plan details</h4>
								<div className="tr-plan-row">
									<span>Plan name</span>
									<strong>{displayText(selectedTripolaPlan.plan_name)}</strong>
								</div>
								<div className="tr-plan-row">
									<span>Targeted users</span>
									<strong>
										{displayText(selectedTripolaPlan.targeted_users)}
									</strong>
								</div>
								<div className="tr-plan-row">
									<span>Monthly price</span>
									<strong
										className={
											billingCycle === "monthly" ? "is-accent" : ""
										}
									>
										{formatMoney(
											selectedTripolaPlan.monthly_price,
											currencySymbol
										)}
									</strong>
								</div>
								<div className="tr-plan-row">
									<span>Yearly price</span>
									<strong
										className={
											billingCycle === "yearly" ? "is-accent" : ""
										}
									>
										{formatMoney(
											selectedTripolaPlan.yearly_price,
											currencySymbol
										)}
									</strong>
								</div>
							</div>

							<div className="tr-plan-block">
								<h4>Billing summary</h4>
								<div className="tr-plan-row">
									<span>
										<MyCalendarIcon
											fontSize="small"
											sx={{ verticalAlign: "middle", mr: 0.5, fontSize: 16 }}
										/>
										Billing cycle
									</span>
									<strong>
										{billingCycle
											? billingCycle.charAt(0).toUpperCase() +
												billingCycle.slice(1)
											: "-"}
									</strong>
								</div>
								<div className="tr-plan-row">
									<span>Selected price</span>
									<strong className="is-accent">
										{formatMoney(activePrice, currencySymbol)}
									</strong>
								</div>
								<div className="tr-plan-row">
									<span>Auto pay</span>
									<strong>
										{formik.values.tripolaIsAutoPay ? "Enabled" : "Disabled"}
									</strong>
								</div>
							</div>

							{featureLabels.length > 0 && (
								<div className="tr-plan-block">
									<h4>Features</h4>
									<ul className="tr-plan-features">
										{featureLabels.map((feature, index) => (
											<li key={`${feature}-${index}`}>{feature}</li>
										))}
									</ul>
								</div>
							)}
						</>
					)}
				</div>
			</aside>
		</div>
	);
};

export default TripolaPlanStructureStep;
